import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiService from "../../services/ApiService";
import { User, LoginCredentials } from "../../model/UserData";
import { canUseAdmin } from "../../common/enum/Role";

// Auth state interface
interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  token: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  token: null,
};

// Login response interface for Redux state
export interface LoginResponse {
  user: User;
  token: string;
}

// Async thunk for login
export const loginUser = createAsyncThunk<
  LoginResponse,
  LoginCredentials,
  { rejectValue: string }
>("auth/loginUser", async (credentials, { rejectWithValue }) => {
  try {
    // Use ApiService for login
    const response = await apiService.login(
      credentials.email,
      credentials.password
    );

    if (response.success && response.data) {
      const { id, email, name, access_token, role, schoolId } = response.data;

      if (!canUseAdmin(role?.name)) {
        return rejectWithValue("Tài khoản không có quyền truy cập trang quản trị");
      }

      // Create simplified user object for Redux state
      const authenticatedUser: User = {
        id: id || "",
        email: email,
        name: name || "",
        role: role?.name,
        schoolId: schoolId ?? null,
      };
      console.log("Authenticated User:", authenticatedUser); // Debug log
      const token = access_token;
      // Store user data and token in localStorage
      localStorage.setItem("user", JSON.stringify(authenticatedUser));
      localStorage.setItem("token", token);
        
      return {
        user: authenticatedUser,
        token: token,
      };
    } else {
      return rejectWithValue(response.message || "Login failed");
    }
  } catch (error: any) {
    // Handle different types of API errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        "Login failed";

      if (status === 401) {
        return rejectWithValue("Invalid email or password");
      } else if (status === 429) {
        return rejectWithValue(
          "Too many login attempts. Please try again later."
        );
      } else if (status >= 500) {
        return rejectWithValue("Server error. Please try again later.");
      } else {
        return rejectWithValue(message);
      }
    } else if (error.request) {
      // Request made but no response received
      return rejectWithValue("Network error. Please check your connection.");
    } else {
      // Something else happened
      return rejectWithValue(error.message || "An unexpected error occurred");
    }
  }
});

// Async thunk for logout
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      // Call API service to logout (invalidate token on server)
      // await apiService.logout();

      // Clear localStorage
      console.log("Logging out user, clearing localStorage"); // Debug log
      localStorage.removeItem("user");
      localStorage.removeItem("token");

      return null;
    } catch (error: any) {
      // Even if API call fails, clear local storage
      localStorage.removeItem("user");
      localStorage.removeItem("token");

      // Log the error but don't reject - logout should always succeed locally
      console.warn("Logout API call failed:", error.message);
      return null;
    }
  }
);

// Async thunk for checking auth status (initialization)
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { rejectWithValue }) => {
    try {
      const storedToken = localStorage.getItem("token");

      if (!storedToken) {
        return null;
      }

      // Validate token with server by fetching current user
      const response = await apiService.getCurrentUser();

      if (response.success && response.data) {
        // Update localStorage with fresh user data
        localStorage.setItem("user", JSON.stringify(response.data));
        return {
          user: response.data,
          token: storedToken,
        };
      } else {
        // Token is invalid, clear local storage
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        return null;
      }
    } catch (error: any) {
      // Clear invalid tokens on any error
      localStorage.removeItem("user");
      localStorage.removeItem("token");

      // Return null for auth check failures (not rejected)
      // This allows app to gracefully handle unauthenticated state
      return null;
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set loading state manually if needed
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Update user profile
    updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    // Login cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload || "Login failed";
      })

      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
        state.isLoading = false;
      })

      // Check auth status cases
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          // Ensure user object matches User type
          const userData = action.payload.user;
          state.user = {
            id: userData.id || "",
            email: userData.email,
            name: userData.name || "",
            role: userData.role?.name,
            schoolId: userData.schoolId ?? null,
          };
          state.token = action.payload.token;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
        }
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });
  },
});

// Export actions
export const { clearError, setLoading, updateUserProfile } = authSlice.actions;

// Export selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectError = (state: { auth: AuthState }) => state.auth.error;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;

// Export reducer
export default authSlice.reducer;
